/**
 * @file billSupplierMatch.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description The supplier an uploaded bill names against the PO's (Jade,
 *              5 Oct 2026). The server reads the name from the bill's text and
 *              matches it to a supplier contact with its supplier group
 *              (POST /api/bills/parse-pdf → suggested.supplier_match); this
 *              says whether that disagrees with the PO's supplier.
 *
 * Not a disagreement: the same contact, or one supplier group — a PO from
 * "MVFS - Risdon" billed by "Mole Valley Feed Solutions" is one supplier
 * (customers.supplier_groups; the server's utils/billRules.sameSupplier).
 */

/**
 * @param {object|null} match — suggested.supplier_match: { account_id, company_name,
 *        printed_name, group_account_ids, xero_contact_id }
 * @param {{ accountId: number|string|null, name?: string }} po — the PO's supplier
 * @returns {null | { accountId: number, companyName: string, printedName: string,
 *   xeroContactId: string|null, poName: string }}
 */
export function supplierClash(match, po) {
  if (!match || !match.account_id || !po || !po.accountId) return null;
  const a = Number(match.account_id);
  const p = Number(po.accountId);
  if (a === p) return null;
  if ((match.group_account_ids || []).map(Number).includes(p)) return null;
  return {
    accountId: a,
    companyName: match.company_name || '',
    printedName: match.printed_name || match.company_name || '',
    xeroContactId: match.xero_contact_id || null,
    poName: po.name || '',
  };
}

/** The invoice's supplier, in the reconciler's words. */
export function invoiceSupplierText(clash) {
  if (!clash) return '';
  const printed = clash.printedName && clash.printedName !== clash.companyName ? ` (“${clash.printedName}” on the invoice)` : '';
  return `${clash.companyName}${printed}`;
}

/**
 * When the name on the bill matched no supplier contact, what to tell the
 * user, or null.
 */
export function unmatchedSupplierNote(match) {
  if (!match || match.account_id || !match.printed_name) return null;
  const could = match.ambiguous && match.candidates?.length
    ? ` It could be ${match.candidates.map(c => c.company_name).join(' or ')}.` : '';
  return `The invoice is from “${match.printed_name}”, which matches no single supplier contact.${could} Check the supplier.`;
}
